/**
 * tokens/config.js - Canonical token configuration for Page Builder
 * Location: /app/frontend/src/admin/page_builder/tokens/
 * 
 * Token refs are stored as: {$token: "colors.primary"}
 * Token paths use dot notation: "colors.primary", "typography.heading.fontFamily"
 * 
 * @typedef {Object} TokenRef
 * @property {string} $token - Dot-path to token value (e.g., "colors.primary")
 */

/**
 * Default design tokens - Trap Comix brand
 * Dark bg, orange primary, subtle glow aesthetic
 */
export const defaultTokens = {
  colors: {
    // Backgrounds
    bg: '#000000',
    surface: '#18181b',
    surfaceHover: '#27272a',
    surfaceActive: '#3f3f46',
    
    // Text
    text: '#ffffff',
    textMuted: '#a1a1aa',
    textSubtle: '#71717a',
    
    // Brand - Primary (Orange)
    primary: '#f97316',
    primaryHover: '#ea580c',
    primaryMuted: 'rgba(249, 115, 22, 0.2)',
    primaryGlow: 'rgba(249, 115, 22, 0.3)',
    
    // Brand - Accent (Red)
    accent: '#ef4444',
    accentHover: '#dc2626',
    accentMuted: 'rgba(239, 68, 68, 0.2)',
    
    // Borders
    border: '#3f3f46',
    borderSubtle: '#27272a',
    borderFocus: '#f97316',
    
    // Semantic
    success: '#22c55e',
    warning: '#eab308',
    error: '#ef4444',
    info: '#3b82f6'
  },
  
  typography: {
    // Font families
    fontFamily: {
      display: "'Inter', system-ui, sans-serif",
      heading: "'Inter', system-ui, sans-serif",
      body: "'Inter', system-ui, sans-serif",
      mono: "'JetBrains Mono', 'Fira Code', monospace"
    },
    
    // Font sizes
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
      '6xl': '3.75rem'
    },
    
    // Font weights
    fontWeight: {
      light: '300',
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      black: '900'
    },
    
    // Line heights
    lineHeight: {
      none: '1',
      tight: '1.1',
      snug: '1.25',
      normal: '1.5',
      relaxed: '1.75',
      loose: '2'
    },
    
    // Letter spacing
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em'
    }
  },
  
  spacing: {
    '0': '0',
    '1': '0.25rem',
    '2': '0.5rem',
    '3': '0.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '8': '2rem',
    '10': '2.5rem',
    '12': '3rem',
    '16': '4rem',
    '20': '5rem',
    '24': '6rem',
    '32': '8rem',
    '40': '10rem',
    '48': '12rem',
    '56': '14rem',
    '64': '16rem'
  },
  
  radius: {
    none: '0',
    sm: '0.125rem',
    default: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px'
  },
  
  shadow: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    glow: '0 0 20px rgba(249, 115, 22, 0.3)',
    glowLg: '0 0 40px rgba(249, 115, 22, 0.4)'
  }
};

/**
 * Get a token value by dot-path string
 * @param {string} tokenPath - Dot-notation path (e.g., "colors.primary", "typography.fontFamily.heading")
 * @param {object} tokens - Token object to search (defaults to defaultTokens)
 * @returns {*} Token value or undefined if not found
 */
export function getTokenValue(tokenPath, tokens = defaultTokens) {
  if (!tokenPath || typeof tokenPath !== 'string') {
    return undefined;
  }
  
  const parts = tokenPath.split('.');
  let current = tokens;
  
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  
  return current;
}

/**
 * Check if a value is a token reference
 * @param {*} value - Value to check
 * @returns {boolean} True if value is a token ref object
 */
export function isTokenRef(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.$token === 'string'
  );
}

/**
 * Create a token reference object
 * @param {string} tokenPath - Dot-notation path
 * @returns {TokenRef} Token reference object
 */
export function createTokenRef(tokenPath) {
  return { $token: tokenPath };
}

/**
 * Extract token path from a token ref
 * @param {TokenRef} tokenRef - Token reference object
 * @returns {string|null} Token path or null if not a valid ref
 */
export function getTokenPath(tokenRef) {
  if (isTokenRef(tokenRef)) {
    return tokenRef.$token;
  }
  return null;
}

/**
 * Convert a token path to CSS variable name
 * e.g., "colors.primary" -> "--tc-colors-primary"
 * @param {string} tokenPath - Dot-notation path
 * @returns {string} CSS variable name
 */
export function tokenPathToCSSVar(tokenPath) {
  return `--tc-${tokenPath.replace(/\./g, '-')}`;
}

/**
 * Convert a CSS variable name back to token path
 * e.g., "--tc-colors-primary" -> "colors.primary"
 * @param {string} cssVar - CSS variable name
 * @returns {string|null} Token path or null if not a valid var
 */
export function cssVarToTokenPath(cssVar) {
  if (!cssVar || !cssVar.startsWith('--tc-')) {
    return null;
  }
  return cssVar.slice(5).replace(/-/g, '.');
}

export default {
  defaultTokens,
  getTokenValue,
  isTokenRef,
  createTokenRef,
  getTokenPath,
  tokenPathToCSSVar,
  cssVarToTokenPath
};
